import React, { useState, useEffect } from 'react';
import './AddEmployees.css';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTimes } from "react-icons/fa";
import AddNewEmployees from './AddNewEmployees';
import EditEmployees from './EditEmployees';
import { API_URL } from "./API";

const isCEO = () => {
  const accessLevel = localStorage.getItem('accessLevel');
  const userRole = localStorage.getItem('currentUserRole') || '';
  return accessLevel === 'full' || userRole.toUpperCase().includes('CEO');
};

const ProfileAvatar = ({ employee, size = 'default' }) => {
  const [imageError, setImageError] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  const sizeClass = size === 'large' ? 'EMP-avatar-large' : 'EMP-26';
  const placeholderClass = size === 'large' ? 'EMP-avatar-placeholder-large' : 'EMP-27';

  useEffect(() => {
    const employeeId = employee.id || employee.employee_id;
    if (employeeId) {
      const storedImage = localStorage.getItem(`employee_${employeeId}_profileImage`);
      setProfileImage(storedImage);
      setImageError(!storedImage);
    }
  }, [employee.id, employee.employee_id]);

  const hasValidImage = profileImage && profileImage !== '' && !imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  const getInitials = (name) => {
    if (!name || name === 'N/A') return '?';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const getBackgroundColor = (name) => {
    if (!name || name === 'N/A') return '#6B7280';
    
    const colors = [
      '#EF4444', '#F97316', '#F59E0B', '#EAB308', 
      '#84CC16', '#22C55E', '#10B981', '#14B8A6',
      '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
      '#8B5CF6', '#A855F7', '#D946EF', '#EC4899'
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  if (hasValidImage) {
    return (
      <div className={sizeClass}>
        <img
          src={profileImage}
          alt={`${employee.name} profile`}
          className="EMP-profile-image"
          onError={handleImageError}
          onLoad={() => setImageError(false)}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
      </div>
    );
  }

  return (
    <div className={sizeClass}>
      <span 
        className={placeholderClass}
        style={{ 
          backgroundColor: getBackgroundColor(employee.name),
          color: 'white',
          fontWeight: '600'
        }}
      >
        {getInitials(employee.name)}
      </span>
    </div>
  );
};

const EmployeeViewModal = ({ employee, onClose, onEdit }) => {
  const currentUserId = localStorage.getItem('currentUserId');
  const accessLevel = localStorage.getItem('accessLevel');
  const isCEOUser = accessLevel === 'full';
  const isOwnProfile = employee.id.toString() === currentUserId;
  
  const showFullDetails = isCEOUser || isOwnProfile;
  
  return (
    <div className="employee-view-overlay">
      <div className="employee-view-modal">
        <div className="employee-view-header">
          <h2>Employee Details</h2>
          <FaTimes className="close-icon" onClick={onClose} />
        </div>
        
        <div className="employee-view-content">
          <div className="employee-profile-section">
            <ProfileAvatar employee={employee} size="large" />
            <div className="employee-basic-info">
              <h3>{employee.name || 'N/A'}</h3>
              <p className="employee-id">ID: {employee.id || 'N/A'}</p>
              <p className="employee-role">{employee.role || 'N/A'}</p>
              <span className={`status-badge ${(employee.status || 'active').toLowerCase()}`}>
                {employee.status || 'Active'}
              </span>
            </div>
          </div>

          <div className="employee-details-grid">
            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-item">
                <label>Email:</label>
                <span>{employee.email || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Phone:</label>
                <span>{employee.phone || 'N/A'}</span>
              </div>
            </div>

            {showFullDetails && (
              <>
                <div className="detail-section">
                  <h4>Identity Information</h4>
                  <div className="detail-item">
                    <label>PAN Number:</label>
                    <span>{employee.panNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Aadhar Number:</label>
                    <span>{employee.aadharNumber || 'N/A'}</span>
                  </div>
                </div>

                {employee.bankDetails && (
                  <div className="detail-section">
                    <h4>Bank Details</h4>
                    <div className="detail-item">
                      <label>Account Name:</label>
                      <span>{employee.bankDetails.accountName || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Account Number:</label>
                      <span>{employee.bankDetails.accountNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>IFSC Code:</label>
                      <span>{employee.bankDetails.ifscCode || 'N/A'}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="employee-view-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {(isCEOUser || isOwnProfile) && (
            <button 
              className="btn-primary" 
              onClick={() => {
                onClose();
                onEdit(employee.id);
              }}
            >
              Edit Employee
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EmployeeManagement = ({ 
  onCurrentEmployeeUpdate, 
  selectedEmployeeStatus, 
  onStatusChange,
  onDeleteEmployee 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);

  const updateCurrentEmployeeInNavbar = (employeesData) => {
    const currentUserEmail = localStorage.getItem('currentUserEmail');
    const currentUserId = localStorage.getItem('currentUserId');
    const storedUser = localStorage.getItem('currentUser');
    
    let currentEmployee = null;
    
    if (currentUserEmail) {
      currentEmployee = employeesData.find(emp => 
        emp.email && emp.email.toLowerCase() === currentUserEmail.toLowerCase()
      );
    }
    
    if (!currentEmployee && currentUserId) {
      currentEmployee = employeesData.find(emp => 
        (emp.id && emp.id.toString() === currentUserId) ||
        (emp.employee_id && emp.employee_id.toString() === currentUserId)
      );
    }
    
    if (!currentEmployee && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.email) {
          currentEmployee = employeesData.find(emp => 
            emp.email && emp.email.toLowerCase() === userData.email.toLowerCase()
          );
        }
      } catch (error) {
       
      }
    }
    
    if (currentEmployee && onCurrentEmployeeUpdate) {
      const employeeId = currentEmployee.id || currentEmployee.employee_id;
      const storedImage = localStorage.getItem(`employee_${employeeId}_profileImage`);
      
      const currentEmployeeData = {
        id: employeeId,
        name: currentEmployee.name,
        email: currentEmployee.email,
        role: currentEmployee.role,
        status: currentEmployee.status || 'Active',
        profileImage: storedImage || currentEmployee.profileImage,
        employee_id: employeeId
      };
      
      localStorage.setItem('currentUser', JSON.stringify(currentEmployeeData));
      localStorage.setItem('currentUserName', currentEmployee.name);
      localStorage.setItem('currentUserId', employeeId.toString());
      localStorage.setItem('currentUserEmail', currentEmployee.email);
      
      onCurrentEmployeeUpdate(currentEmployeeData);
    }
  };

  const loadProfileImagesFromStorage = (employeesData) => {
    const processedEmployees = employeesData.map(employee => {
      const employeeId = employee.employee_id || employee.id;
      const storedImage = localStorage.getItem(`employee_${employeeId}_profileImage`);
      return {
        ...employee,
        id: employeeId,
        name: employee.name || 'N/A',
        role: employee.role || 'N/A',
        email: employee.email || 'N/A',
        phone: employee.phone_number || employee.phone || 'N/A',
        status: employee.status || 'Active',
        profileImage: storedImage,
        avatar: storedImage || '👤'
      };
    });
    
    return processedEmployees;
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const accessLevel = localStorage.getItem('accessLevel');
      const isCEOUser = accessLevel === 'full';
      
      let url = `${API_URL}/employees/get_employees`;
      const params = new URLSearchParams();
      
      if (selectedEmployeeStatus && selectedEmployeeStatus !== "") {
        params.append('status', selectedEmployeeStatus);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
       
        if (data.employees && data.employees.length > 0) {
        
        }
        
        if (data && data.employees && Array.isArray(data.employees)) {
          const employeesWithImages = loadProfileImagesFromStorage(data.employees);

          employeesWithImages.sort((a, b) => {
            const idA = parseInt(a.employee_id || a.id) || 0;
            const idB = parseInt(b.employee_id || b.id) || 0;
            return idA - idB;
          });
          
   
          setEmployees(employeesWithImages);
          updateCurrentEmployeeInNavbar(employeesWithImages);
          
          if (isCEOUser) {
            localStorage.setItem('allEmployeesData', JSON.stringify(data.employees));
          }
        } else {
          
          setEmployees([]);
        }
      } else {
        
        const errorText = await response.text();
     
        setEmployees([]);
      }
    } catch (error) {
      
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };


  
  useEffect(() => {
    fetchEmployees();
  }, [selectedEmployeeStatus]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      updateCurrentEmployeeInNavbar(employees);
    }
  }, [employees]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddEmployee = () => {
    setShowAddEmployee(true);
  };
  const handleCloseViewEmployee = () => {
    setShowViewEmployee(false);
    setSelectedEmployee(null);
  };

  const handleBackToEmployeeList = () => {
    setShowAddEmployee(false);
    setShowEditEmployee(false);
    setShowViewEmployee(false);
    setSelectedEmployee(null);
    fetchEmployees();
  };

  const handleAddNewEmployee = (newEmployee) => {
    const employeeId = newEmployee.id || Date.now();
    const employeeWithId = { ...newEmployee, id: employeeId };
    
    if (employeeWithId.profileImage) {
      localStorage.setItem(`employee_${employeeId}_profileImage`, employeeWithId.profileImage);
    }
    
    const updatedEmployees = [...employees, employeeWithId];
    setEmployees(updatedEmployees);
    updateCurrentEmployeeInNavbar(updatedEmployees);
    setShowAddEmployee(false);
  };

  const handleEdit = (id) => {
    const employee = employees.find(emp => emp.id === id);
    if (employee) {
      const storedImage = localStorage.getItem(`employee_${id}_profileImage`);
      const employeeWithImage = {
        ...employee,
        profileImage: storedImage || employee.profileImage
      };
      setSelectedEmployee(employeeWithImage);
      setShowEditEmployee(true);
    }
  };

  const handleView = (id) => {
    const employee = employees.find(emp => emp.id === id);
    if (employee) {
      const storedImage = localStorage.getItem(`employee_${id}_profileImage`);
      const employeeWithImage = {
        ...employee,
        profileImage: storedImage || employee.profileImage
      };
      setSelectedEmployee(employeeWithImage);
      setShowViewEmployee(true);
    }
  };

  const handleDelete = async (id) => {
    const employee = employees.find(emp => emp.id === id);
    if (!employee) return;

    const isConfirmed = window.confirm(
      `Are you sure you want to mark "${employee.name}" as Inactive?`
    );
    if (!isConfirmed) return;

    try {
      const response = await fetch(`${API_URL}/employees/delete_employees`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employee_id: id.toString()
        }),
      });

      const result = await response.json();

      if (response.ok && result.bool) {
        localStorage.removeItem('allEmployeesData');
        
        const updatedEmployees = employees.map(emp =>
          emp.id === id ? { ...emp, status: 'Inactive' } : emp
        );
        setEmployees(updatedEmployees);
        updateCurrentEmployeeInNavbar(updatedEmployees);
        
        await fetchEmployees();
        
        alert(result.message || 'Employee marked as Inactive successfully!');
      } else {
        throw new Error(result.message || 'Failed to update employee status');
      }
    } catch (error) {
      
      
      localStorage.removeItem('allEmployeesData');
      
      const updatedEmployees = employees.map(emp =>
        emp.id === id ? { ...emp, status: 'Inactive' } : emp
      );
      setEmployees(updatedEmployees);
      updateCurrentEmployeeInNavbar(updatedEmployees);
      
      alert('Employee marked as Inactive (local update only - backend error occurred)');
    }
  };
 
  const handleUpdateEmployee = async (updatedEmployee) => {
    try {
      if (updatedEmployee.profileImage && updatedEmployee.id) {
        localStorage.setItem(`employee_${updatedEmployee.id}_profileImage`, updatedEmployee.profileImage);
      }

      await fetchEmployees();
      
      if (onCurrentEmployeeUpdate) {
        const allEmployees = employees;
        updateCurrentEmployeeInNavbar(allEmployees);
      }
    } catch (error) {
  
    }
    
    setShowEditEmployee(false);
    setSelectedEmployee(null);
  };

  if (showAddEmployee) {
    return (
      <AddNewEmployees 
        onBack={handleBackToEmployeeList} 
        onAddEmployee={handleAddNewEmployee}
      />
    );
  }

  if (showEditEmployee && selectedEmployee) {
    return (
      <EditEmployees
        employee={selectedEmployee} 
        onBack={handleBackToEmployeeList}
        onUpdate={handleUpdateEmployee}
      />
    );
  }

  const filteredEmployees = employees.filter(employee => {
    const currentUserId = localStorage.getItem('currentUserId');
    const accessLevel = localStorage.getItem('accessLevel');
    const isCEOUser = accessLevel === 'full';

    const matchesSearch = 
      (employee.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.id || employee.employee_id || '').toString().includes(searchTerm) ||
      (employee.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.phone || employee.phone_number || '').includes(searchTerm);
    
    if (isCEOUser) {
      return matchesSearch;
    }
    
    return matchesSearch;
  });

  const isCurrentUser = (employeeId) => {
    const currentUserId = localStorage.getItem('currentUserId');
    return employeeId.toString() === currentUserId;
  };

  return (
    <div className="EMP-00">
      <div className="EMP-08">
        {isCEO() && (
          <>
            <div className="EMP-09">
              <input
                type="text"
                placeholder="Employee Details"
                value={searchTerm}
                onChange={handleSearch}
                className="EMP-10"
              />
              <FaSearch className="EMP-11" />
            </div>

            <div className="status-filter-container">
              <select 
                value={selectedEmployeeStatus} 
                onChange={(e) => onStatusChange(e.target.value)}
                className="status-filter-dropdown"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button className="EMP-12" onClick={handleAddEmployee}>
              +Add New Employee
            </button>
          </>
        )}
      </div>

      <div className="EMP-13">
        {loading && <div className="loading-message">Loading employees...</div>}
        
        <table className="EMP-14">
          <thead className="EMP-15">
            <tr className="EMP-16">
              <th className="EMP-17">PROFILE</th>
              <th className="EMP-18">EMPLOYEE ID</th>
              <th className="EMP-19">EMPLOYEE NAME</th>
              <th className="EMP-20">ROLE</th>
              <th className="EMP-21">STATUS</th>
              <th className="EMP-22">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="EMP-23">
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee, index) => (
                <tr key={employee.id} className={`EMP-24 EMP-24-${index}`}>
                  <td className="EMP-25">
                    <ProfileAvatar employee={employee} />
                  </td>
                  <td className="EMP-28">{employee.id || 'N/A'}</td>
                  <td className="EMP-29">{employee.name || 'N/A'}</td>
                  <td className="EMP-30">{employee.role || 'N/A'}</td>
                  <td className="EMP-31">
                    <span className={`EMP-32 status-${(employee.status || 'active').toLowerCase()}`}>
                      {employee.status || 'Active'}
                    </span>
                  </td>
                  <td className="EMP-33">
                    <div className="EMP-34">
                      <FaEye 
                        className="EMP-36" 
                        title="View Employee" 
                        onClick={() => handleView(employee.id)}
                        style={{ fontSize: '30px', margin: '0 8px', cursor: 'pointer', color: '#000' }}
                      />
                      {(isCEO() || isCurrentUser(employee.id)) && (
                        <FaEdit   
                          className="EMP-35" 
                          title="Edit Employee"
                          onClick={() => handleEdit(employee.id)}
                          style={{ 
                            fontSize: '30px', 
                            margin: '0 8px',
                            color: '#000', 
                            cursor: 'pointer'
                          }}
                        />
                      )}
                      {isCEO() && (
                        <FaTrash 
                          className="EMP-37" 
                          title="Mark as Inactive"
                          onClick={() => handleDelete(employee.id)}
                          style={{ 
                            fontSize: '30px', 
                            margin: '0 8px', 
                            cursor: 'pointer',
                            color: '#000'
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-employees">
                  {searchTerm ? 'No employees found matching your search' : 'No employees found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showViewEmployee && selectedEmployee && (
        <EmployeeViewModal 
          employee={selectedEmployee} 
          onClose={handleCloseViewEmployee}
          onEdit={handleEdit}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;